'use client';

/**
 * SlideOverShell — compatibility adapter for the person-detail slide-over
 * panels (UserDetailSlideOver keyed by user_id, EmployeeDetailSlideOver
 * keyed by employee_id) and their finance consumers.
 *
 * All chrome now lives in the canonical shared component
 * `src/components/SlideOver.tsx` — this adapter only maps the legacy
 * prop names (`icon` → `customIcon`, `accentColor` defaults to
 * actionColor) so existing callers keep working. New slide-overs should
 * import `SlideOver` / `WideSlideOver` / `ConfigSlideOver` directly.
 */

import { type MutableRefObject, type ReactNode } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import SlideOver, { type SlideOverTabItem } from '@/components/SlideOver';

export type { SlideOverTabItem };

interface SlideOverShellProps {
  onClose: () => void;
  /** Header avatar/icon (rendered by the caller so it can be data-driven). */
  icon: ReactNode;
  title: string;
  titleExtra?: ReactNode;
  subtitle?: ReactNode;
  tabs: SlideOverTabItem[];
  activeTab: string;
  onTabChange: (id: string) => void;
  loading?: boolean;
  loadingMessage?: string;
  /** Whether loaded content exists — children render only when true. */
  hasContent?: boolean;
  footer?: ReactNode;
  children?: ReactNode;
  /** data-tour-id hooks for guided tours (`*TourId` names so the static
      tour-target audit registers the literal values passed by callers). */
  panelTourId?: string;
  headerTourId?: string;
  tabsTourId?: string;
  contentTourId?: string;
  /** Ref attached to the scrollable content div (for scroll-position restore). */
  scrollRef?: MutableRefObject<HTMLDivElement | null>;
}

export default function SlideOverShell({
  onClose,
  icon,
  title,
  titleExtra,
  subtitle,
  tabs,
  activeTab,
  onTabChange,
  loading = false,
  loadingMessage = 'Loading details',
  hasContent = true,
  footer,
  children,
  panelTourId,
  headerTourId,
  tabsTourId,
  contentTourId,
  scrollRef,
}: SlideOverShellProps) {
  // These panels historically accent with the fixed action blue, not the
  // client branding colour — preserve that by pinning the accent.
  const { actionColor } = useTheme();

  return (
    <SlideOver
      size="wide"
      onClose={onClose}
      customIcon={icon}
      title={title}
      titleExtra={titleExtra}
      subtitle={subtitle}
      accentColor={actionColor}
      showRealtimeStatus={false}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      loading={loading}
      loadingMessage={loadingMessage}
      hasContent={hasContent}
      footer={footer}
      panelTourId={panelTourId}
      headerTourId={headerTourId}
      tabsTourId={tabsTourId}
      contentTourId={contentTourId}
      scrollRef={scrollRef}
    >
      {children}
    </SlideOver>
  );
}
